import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/**
 * Modal for a promoter to add a guest (single or couple) to a selected list.
 * Handles party composition (single M/F, couple M+F / F+F) and mandatory
 * partner details when party_type = "couple".
 *
 * Props:
 *   open, onOpenChange   — dialog state
 *   guestForm, setGuestForm  — form state (name, phone, email, birth_date, party_type, gender, partner_*, consents)
 *   onSubmit             — form submit handler
 */
export default function AddGuestDialog({ open, onOpenChange, guestForm, setGuestForm, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Josefin_Sans'] text-2xl">ADD GUEST</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">FULL NAME *</label>
            <input
              type="text"
              value={guestForm.name}
              onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
              className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
              required
              data-testid="guest-name-input"
            />
          </div>
          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">EMAIL</label>
            <input
              type="email"
              value={guestForm.email}
              onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
              className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
              data-testid="guest-email-input"
            />
          </div>
          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">PHONE (WhatsApp)</label>
            <input
              type="tel"
              value={guestForm.phone}
              onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
              placeholder="+91 99999 99999"
              className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
              data-testid="guest-phone-input"
            />
          </div>
          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">DATE OF BIRTH *</label>
            <input
              type="date"
              min="1900-01-01"
              max={today}
              value={guestForm.birth_date}
              onChange={(e) => setGuestForm({ ...guestForm, birth_date: e.target.value })}
              className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
              required
              data-testid="guest-birth-date-input"
            />
            <p className="text-zinc-500 text-xs mt-1 font-mono">
              Server rejects entries below the event&apos;s minimum age.
            </p>
          </div>

          <div>
            <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">PARTY *</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[{ v: 'single', l: 'SINGLE' }, { v: 'couple', l: 'COUPLE' }].map((o) => {
                const active = guestForm.party_type === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setGuestForm({ ...guestForm, party_type: o.v, gender: '' })}
                    className={`p-2.5 border font-mono text-xs tracking-widest ${
                      active ? 'border-[#B5FF00] bg-[#B5FF00]/5 text-[#B5FF00]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                    data-testid={`guest-party-${o.v}`}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
            {guestForm.party_type === 'single' ? (
              <div className="grid grid-cols-2 gap-2">
                {[{ v: 'female', l: 'FEMALE' }, { v: 'male', l: 'MALE' }].map((o) => {
                  const active = guestForm.gender === o.v;
                  return (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setGuestForm({ ...guestForm, gender: o.v })}
                      className={`p-2.5 border font-mono text-xs tracking-widest ${
                        active ? 'border-[#B5FF00] bg-[#B5FF00]/5 text-[#B5FF00]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                      }`}
                      data-testid={`guest-gender-${o.v}`}
                    >
                      {o.l}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  {[{ v: 'male_female', l: 'MALE + FEMALE' }, { v: 'female_female', l: 'FEMALE + FEMALE' }].map((o) => {
                    const active = guestForm.gender === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setGuestForm({ ...guestForm, gender: o.v })}
                        className={`p-2.5 border font-mono text-xs tracking-widest ${
                          active ? 'border-[#B5FF00] bg-[#B5FF00]/5 text-[#B5FF00]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid={`guest-couple-${o.v}`}
                      >
                        {o.l}
                      </button>
                    );
                  })}
                </div>
                <h4
                  className="font-['Josefin_Sans'] text-base text-white mt-2 mb-2 pt-2 border-t border-zinc-800"
                  data-testid="guest-person2-heading"
                >
                  PERSON 2 · PARTNER DETAILS
                </h4>
                <input
                  type="text"
                  value={guestForm.partner_name}
                  onChange={(e) => setGuestForm({ ...guestForm, partner_name: e.target.value })}
                  placeholder="Second person's full name *"
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  data-testid="guest-partner-name-input"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  <input
                    type="tel"
                    value={guestForm.partner_phone}
                    onChange={(e) => setGuestForm({ ...guestForm, partner_phone: e.target.value })}
                    placeholder="Partner phone (WhatsApp) *"
                    className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                    data-testid="guest-partner-phone-input"
                  />
                  <input
                    type="email"
                    value={guestForm.partner_email}
                    onChange={(e) => setGuestForm({ ...guestForm, partner_email: e.target.value })}
                    placeholder="Partner email (optional)"
                    className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                    data-testid="guest-partner-email-input"
                  />
                </div>
                <div className="mt-2">
                  <label className="font-mono text-[10px] text-zinc-500 tracking-widest mb-1 block">
                    PARTNER DATE OF BIRTH *
                  </label>
                  <input
                    type="date"
                    min="1900-01-01"
                    max={today}
                    value={guestForm.partner_birth_date}
                    onChange={(e) => setGuestForm({ ...guestForm, partner_birth_date: e.target.value })}
                    className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                    data-testid="guest-partner-birth-date-input"
                  />
                </div>
              </>
            )}
            {guestForm.party_type === 'single' && guestForm.gender === 'male' && (
              <p className="text-amber-400 text-xs mt-2 font-mono">
                ⚠ Solo males go to WAITLIST. Approve them below once you&apos;re happy.
              </p>
            )}
          </div>

          <div className="border-t border-zinc-800 pt-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={guestForm.agreed_eligibility}
                onChange={(e) => setGuestForm({ ...guestForm, agreed_eligibility: e.target.checked })}
                className="mt-1 w-5 h-5 flex-shrink-0"
                data-testid="guest-eligibility-checkbox"
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                Guest confirms they meet the event&apos;s age requirement and have valid ID.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={guestForm.agreed_terms}
                onChange={(e) => setGuestForm({ ...guestForm, agreed_terms: e.target.checked })}
                className="mt-1 w-5 h-5 flex-shrink-0"
                data-testid="guest-terms-checkbox"
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                Guest has read &amp; accepts the event&apos;s Terms &amp; Conditions.
              </span>
            </label>
          </div>

          <div className="flex gap-4 pt-4">
            <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary flex-1">CANCEL</button>
            <button type="submit" className="btn-primary flex-1" data-testid="save-guest-btn">ADD GUEST</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
